/**
 * Pebble in-app notification service.
 *
 * Stores notifications in: notifications/{autoId}
 *   recipientId:   string  — uid of the user who should see this
 *   recipientType: 'eo' | 'mps'
 *   type:          string  — see NOTIFICATION_TYPES below
 *   title:         string
 *   message:       string
 *   eventId:       string | null
 *   documentId:    string | null
 *   linkPath:      string | null  — client-side route to navigate on click
 *   isRead:        boolean
 *   createdAt:     Timestamp
 *
 * Production note: add a composite Firestore index on
 *   (recipientId, recipientType, createdAt DESC) and
 *   (recipientId, recipientType, isRead, createdAt DESC)
 */
import { db } from '../firebase';
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  updateDoc,
  doc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';

export const NOTIFICATION_TYPES = {
  DOCUMENT_SIGNING_REQUIRED: 'document_signing_required',
  DOCUMENT_SIGNED:           'document_signed',
  DOCUMENT_ACKNOWLEDGED:     'document_acknowledged',
  DOCUMENT_REMINDER:         'document_reminder',
  ANNOUNCEMENT:              'announcement',
  VENDOR_PAID:               'vendor_paid',
};

/**
 * Write a notification for a single recipient.
 */
export const createNotification = async ({
  recipientId,
  recipientType,
  type,
  title,
  message,
  eventId    = null,
  documentId = null,
  linkPath   = null,
}) => {
  try {
    await addDoc(collection(db, 'notifications'), {
      recipientId,
      recipientType,
      type,
      title,
      message,
      eventId,
      documentId,
      linkPath,
      isRead: false,
      createdAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error creating notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Write one notification per recipient (bulk).
 */
export const createNotificationsForMany = async (recipientIds, payload) => {
  await Promise.allSettled(
    recipientIds.map((id) => createNotification({ ...payload, recipientId: id }))
  );
};

/**
 * Fetch recent notifications for a user (newest first).
 */
export const getNotifications = async (recipientId, recipientType, maxItems = 30) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', recipientId),
      where('recipientType', '==', recipientType),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
    const snapshot = await getDocs(q);
    return {
      success: true,
      notifications: snapshot.docs.map((d) => ({ id: d.id, ...d.data() })),
    };
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return { success: false, notifications: [], error: error.message };
  }
};

/**
 * Get the count of unread notifications for a user.
 */
export const getUnreadCount = async (recipientId, recipientType) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', recipientId),
      where('recipientType', '==', recipientType),
      where('isRead', '==', false)
    );
    const snapshot = await getDocs(q);
    return { success: true, count: snapshot.size };
  } catch {
    return { success: true, count: 0 };
  }
};

/**
 * Mark a single notification as read.
 */
export const markNotificationRead = async (notificationId) => {
  try {
    await updateDoc(doc(db, 'notifications', notificationId), { isRead: true });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Mark all of a user's notifications as read.
 */
export const markAllRead = async (recipientId, recipientType) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', recipientId),
      where('recipientType', '==', recipientType),
      where('isRead', '==', false)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return { success: true };
    const batch = writeBatch(db);
    snapshot.docs.forEach((d) => batch.update(d.ref, { isRead: true }));
    await batch.commit();
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};
